import { Injectable } from '@angular/core';
import { MessageListTypeEnum } from '../../../views/messaging/enums/message-list-type.enum';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthState } from 'src/shared/store/state/auth.state';

import { IMessage, IMessageFilters, IMessages, IUnreadMessagesDTO } from '../../interfaces/messaging.interface';
import { MessagingService } from '../../services/messaging.service';
import {
    ArchiveMessage,
    CleanCurrentMessage,
    GetArchivedMessages,
    GetMessageById,
    GetReceivedMessages,
    GetSendedMessages,
    GetUnreadMessagesCount,
    MarkAsViewed,
    ResetMessageList,
    ResetMessages,
    ResetMessagesSearch,
    SearchMessages,
    SendMessage,
    SetCurrentMessage,
    UnarchiveMessage,
} from '../actions/messaging.action';

export const itemsByPage: number = 10;

export interface MessagingStateModel {
    sended: IMessage[];
    received: IMessage[];
    archived: IMessage[];
    searchResults: IMessage[];
    sendedPageLoaded: number;
    archivedPageLoaded: number;
    receivedPageLoaded: number;
    searchResultPageLoaded: number;
    sendedTotalItem: number;
    receivedTotalItem: number;
    archivedTotalItem: number;
    searchResultTotalItem: number;
    unreadMessagesCount: number;
    sendedTotalPages: number;
    receivedTotalPages: number;
    archivedTotalPages: number;
    searchResultTotalPages: number;
    currentMessage: IMessage;
}

@State<MessagingStateModel>({
    name: 'messaging',
    defaults: {
        sended: [],
        archived: [],
        received: [],
        searchResults: [],
        sendedPageLoaded: 0,
        archivedPageLoaded: 0,
        receivedPageLoaded: 0,
        searchResultPageLoaded: 0,
        sendedTotalItem: 0,
        receivedTotalItem: 0,
        archivedTotalItem: 0,
        searchResultTotalItem: 0,
        unreadMessagesCount: 0,
        sendedTotalPages: null,
        receivedTotalPages: null,
        archivedTotalPages: null,
        searchResultTotalPages: null,
        currentMessage: null,
    },
})
@Injectable()
export class MessagingState {
    @Selector()
    public static getReceivedMessages(state: MessagingStateModel): IMessage[] {
        return state.received;
    }

    @Selector()
    public static getReceivedMessagesTotalLength(state: MessagingStateModel): number {
        return state.receivedTotalItem;
    }

    @Selector()
    public static getSendedMessages(state: MessagingStateModel): IMessage[] {
        return state.sended;
    }

    @Selector()
    public static getSendedMessagesTotalLength(state: MessagingStateModel): number {
        return state.sendedTotalItem;
    }

    @Selector()
    public static getArchivedMessages(state: MessagingStateModel): IMessage[] {
        return state.archived;
    }

    @Selector()
    public static getArchivedMessagesTotalLength(state: MessagingStateModel): number {
        return state.archivedTotalItem;
    }

    @Selector()
    public static getMessagingSearchResults(state: MessagingStateModel): IMessage[] {
        return state.searchResults;
    }

    @Selector()
    public static getSearchMessagesTotalLength(state: MessagingStateModel): number {
        return state.searchResultTotalItem;
    }

    @Selector()
    public static getNbUnreadMessages(state: MessagingStateModel): number {
        return state.unreadMessagesCount;
    }

    @Selector()
    public static getCurrentMessage(state: MessagingStateModel): IMessage {
        return state.currentMessage;
    }

    constructor(private messagingService: MessagingService, private store: Store) {}

    @Action(GetReceivedMessages)
    public getAllReceivedMessages(ctx: StateContext<MessagingStateModel>, action: GetReceivedMessages): Observable<IMessages> {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const state: MessagingStateModel = ctx.getState();
        if (state.receivedTotalPages === null || state.receivedPageLoaded < state.receivedTotalPages) {
            return this.messagingService
                .getReceivedMessages(state.receivedPageLoaded + 1, itemsByPage, selectedUser, action.filters)
                .pipe(
                tap((result: IMessages) => {
                    if (result) {
                        if (state.receivedTotalPages === null) {
                            ctx.patchState({
                                receivedTotalPages: result.total_pages,
                            });
                        }
                        ctx.patchState({
                            receivedTotalItem: result.total_items,
                            received: [...state.received, ...result.messages],
                            receivedPageLoaded: state.receivedPageLoaded + 1,
                        });
                    }
                })
            );
        }
        return of(null);
    }

    @Action(GetSendedMessages)
    public getAllSendedMessages(ctx: StateContext<MessagingStateModel>, action: GetSendedMessages): Observable<IMessages> {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const state: MessagingStateModel = ctx.getState();
        if (state.sendedTotalPages === null || state.sendedPageLoaded < state.sendedTotalPages) {
            return this.messagingService.getSendedMessages(state.sendedPageLoaded + 1, itemsByPage, selectedUser, action.filters).pipe(
                tap((result: IMessages) => {
                    if (result) {
                        if (state.sendedTotalPages === null) {
                            ctx.patchState({
                                sendedTotalPages: result.total_pages,
                            });
                        }
                        ctx.patchState({
                            sendedTotalItem: result.total_items,
                            sended: [...state.sended, ...result.messages],
                            sendedPageLoaded: state.sendedPageLoaded + 1,
                        });
                    }
                })
            );
        }
        return of(null);
    }

    @Action(GetArchivedMessages)
    public getAllArchivedMessages(ctx: StateContext<MessagingStateModel>, action: GetArchivedMessages): Observable<IMessages> {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const state: MessagingStateModel = ctx.getState();
        if (state.archivedTotalPages === null || state.archivedPageLoaded < state.archivedTotalPages) {
            return this.messagingService
                .getArchivedMessages(state.archivedPageLoaded + 1, itemsByPage, selectedUser, action.filters)
                .pipe(
                tap((result: IMessages) => {
                    if (result) {
                        if (state.archivedTotalPages === null) {
                            ctx.patchState({
                                archivedTotalPages: result.total_pages,
                            });
                        }
                        ctx.patchState({
                            archivedTotalItem: result.total_items,
                            archived: [...state.archived, ...result.messages],
                            archivedPageLoaded: state.archivedPageLoaded + 1,
                        });
                    }
                })
            );
        }
        return of(null);
    }

    @Action(SearchMessages)
    public searchMessages(ctx: StateContext<MessagingStateModel>, action: SearchMessages): Observable<IMessages> {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const state: MessagingStateModel = ctx.getState();

        if (state.searchResultTotalPages === null || state.searchResultPageLoaded < state.searchResultTotalPages) {
            return this.messagingService
                .searchMessages(
                    action.search,
                    action.msgType,
                    state.searchResultPageLoaded + 1,
                    itemsByPage,
                    selectedUser,
                    action.filters
                )
                .pipe(
                tap((result: IMessages) => {
                    if (result) {
                        if (state.searchResultTotalPages === null) {
                            ctx.patchState({
                                searchResultTotalPages: result.total_pages,
                            });
                        }
                        ctx.patchState({
                            searchResultTotalItem: result.total_items,
                            searchResults: [...state.searchResults, ...result.messages],
                            searchResultPageLoaded: state.searchResultPageLoaded + 1,
                        });
                    }
                })
            );
        }
        return of(null);
    }

    @Action(ResetMessageList)
    public resetMessageList(ctx: StateContext<MessagingStateModel>, action: ResetMessageList): void {
        switch (action.listType) {
            case MessageListTypeEnum.RECEIVED:
                ctx.patchState({
                    received: [],
                    receivedPageLoaded: 0,
                    receivedTotalItem: 0,
                    receivedTotalPages: null,
                });
                break;
            case MessageListTypeEnum.SENT:
                ctx.patchState({
                    sended: [],
                    sendedPageLoaded: 0,
                    sendedTotalItem: 0,
                    sendedTotalPages: null,
                });
                break;
            case MessageListTypeEnum.ARCHIVED:
                ctx.patchState({
                    archived: [],
                    archivedPageLoaded: 0,
                    archivedTotalItem: 0,
                    archivedTotalPages: null,
                });
                break;
        }
    }

    @Action(ResetMessagesSearch)
    public resetMessagesSearch(ctx: StateContext<MessagingStateModel>): void {
        ctx.patchState({
            searchResults: [],
            searchResultPageLoaded: 0,
            searchResultTotalItem: 0,
            searchResultTotalPages: null,
        });
    }

    @Action(MarkAsViewed)
    public markAsViewed(ctx: StateContext<MessagingStateModel>, action: MarkAsViewed): Observable<any> {
        return this.messagingService.markAsViewed(action.messageId).pipe(
            tap((result: IMessage) => {
                if (result) {
                    ctx.setState(
                        patch({
                            received: updateItem<IMessage>((msg: IMessage) => msg.id === action.messageId, result),
                            unreadMessagesCount: ctx.getState().unreadMessagesCount - 1,
                        })
                    );
                }
            })
        );
    }

    @Action(ArchiveMessage)
    public archiveMessage(ctx: StateContext<MessagingStateModel>, action: ArchiveMessage): Observable<any> {
        const state: MessagingStateModel = ctx.getState();
        return this.messagingService.archiveMessage(action.messageId).pipe(
            tap((result: IMessage) => {
                if (result) {
                    ctx.setState(
                        patch({
                            received: removeItem<IMessage>((msg: IMessage) => msg.id === action.messageId),
                            archived: [...state.archived, result],
                        })
                    );
                }
            })
        );
    }

    @Action(UnarchiveMessage)
    public unArchiveMessage(ctx: StateContext<MessagingStateModel>, action: ArchiveMessage): Observable<any> {
        const state: MessagingStateModel = ctx.getState();
        return this.messagingService.unArchiveMessage(action.messageId).pipe(
            tap((result: IMessage) => {
                if (result) {
                    ctx.setState(
                        patch({
                            received: [...state.received, result],
                            archived: removeItem<IMessage>((msg: IMessage) => msg.id === action.messageId),
                        })
                    );
                }
            })
        );
    }

    @Action(SendMessage)
    public sendMessage(ctx: StateContext<MessagingStateModel>, action: SendMessage): Observable<any> {
        const state: MessagingStateModel = ctx.getState();
        return this.messagingService.sendMessage(action.message).pipe(
            tap((result: IMessage) => {
                if (result) {
                    ctx.setState(
                        patch({
                            sended: [...state.sended, result],
                        })
                    );
                }
            })
        );
    }

    @Action(GetUnreadMessagesCount)
    public getUnreadMessagesCount(ctx: StateContext<MessagingStateModel>): Observable<IUnreadMessagesDTO> {
        return this.messagingService.getUnreadMessagesCount().pipe(
            tap((result: IUnreadMessagesDTO) => {
                if (result) {
                    ctx.patchState({
                        unreadMessagesCount: result.total_unread,
                    });
                }
            })
        );
    }

    @Action(GetMessageById)
    public getMessageById(ctx: StateContext<MessagingStateModel>, action: GetMessageById): Observable<IMessage> {
        return this.messagingService.getMessageById(action.messageId).pipe(
            tap((result: IMessage) => {
                if (result) {
                    ctx.patchState({
                        currentMessage: result,
                    });
                }
            })
        );
    }

    @Action(CleanCurrentMessage)
    public deleteCurrentMessage(ctx: StateContext<MessagingStateModel>): void {
        ctx.patchState({
            currentMessage: null,
        });
    }

    @Action(SetCurrentMessage)
    public setCurrentMessage(ctx: StateContext<MessagingStateModel>, action: SetCurrentMessage): void {
        ctx.patchState({
            currentMessage: action.message,
        });
    }

    @Action(ResetMessages)
    public resetMessages(ctx: StateContext<MessagingStateModel>): void {
        ctx.patchState({
            sended: [],
            archived: [],
            received: [],
            searchResults: [],
            sendedPageLoaded: 0,
            archivedPageLoaded: 0,
            receivedPageLoaded: 0,
            searchResultPageLoaded: 0,
            sendedTotalItem: 0,
            receivedTotalItem: 0,
            archivedTotalItem: 0,
            searchResultTotalItem: 0,
            unreadMessagesCount: 0,
            sendedTotalPages: null,
            receivedTotalPages: null,
            archivedTotalPages: null,
            searchResultTotalPages: null,
        });
    }
}
